/**
 * @file PrivateChat.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-08-14
 * @description file containing PrivateChat component
 */

import { useState, useRef, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import Messages from "../Chat/Messages";
import ChatInputForm from "../Chat/ChatInputForm";
import UserInfoModal from "../Modal/UserInfoModal";
import {
  fetchPrivateChatMessages,
  sendPrivateChatMessage,
} from "../../utils/privateChatUtils";
import { useUser } from "../../context/UserContext";
import PrivateSettings from "./PrivateSettings";

/**
 * This component displays the private chat room for a specific private chat.
 * It fetches and displays the messages in the chat room.
 * It allows users to send messages, mention other users, and view user information.
 * It also displays the active users in the chat room.
 * When a user is mentioned, a dropdown with user suggestions is displayed.
 * When a user is clicked, a modal with user information is displayed.
 * When the video Url is updated, the video being watched is updated in the chat room.
 * When the private users modal is toggled, the modal is displayed or hidden.
 *
 * @param {Object} props - The component properties
 * @param {string} props.selectedPrivateChat - The object repsenting the selected private chat.
 * @param {string} props.videoUrl - The URL of the video being watched on the page (doesnt necessarily match the private chat video URL)
 * @param {Function} props.updateVideoUrl - Function to update the video URL in the chat room
 * @param {Array} props.selectedPrivateChatMembers - Array of private chat members, each member is an array containing user object and status (accepted, pending, etc.)
 * @param {Function} props.setSelectedPrivateChatMembers - Function to set the selected private chat members.
 * @param {Function} props.setSelectedTab - Function to set the selected tab (youtube, native, private)
 * @param {Function} props.setSelectedPrivateChat - Function to set the selected private chat
 * @param {Boolean} props.loadingChatSettings - State variable tracking if chat settings are loading.
 *
 * @returns {JSX.Element} The PrivateChat component.
 */
const PrivateChat = ({
  selectedPrivateChat,
  videoUrl,
  updateVideoUrl,
  selectedPrivateChatMembers,
  setSelectedPrivateChatMembers,
  selectedPrivateChatPendingMembers,
  setSelectedPrivateChatPendingMembers,
  setSelectedTab,
  setSelectedPrivateChat,
  loadingChatSettings,
}) => {
  const [messages, setMessages] = useState([]); // State variable for the messages in the chat
  const [input, setInput] = useState(""); // State variable for the input message
  const [showOptions, setShowOptions] = useState({}); // State variable for the user options modal
  const [selectedUser, setSelectedUser] = useState(null); // State variable for the selected user
  const [mentionDropdown, setMentionDropdown] = useState(
    selectedPrivateChatMembers,
  ); // State variable for the mention dropdown - holds the filtered users whose display name matches the input
  const messagesEndRef = useRef(null); // Reference to the end of the messages container -> NOTE: useRef() create a mutable object to persist across renders

  const [isPopupOpen, setIsPopupOpen] = useState(true); // State variable for the popup

  const [settingsOpen, setSettingsOpen] = useState(false); // State variable for the settings page for the private chat

  const { user } = useUser();

  // setup message listener when the component mounts
  useEffect(() => {
    console.log("hi");

    if (selectedPrivateChat) {
      const unsubscribeMessages = fetchPrivateChatMessages(
        selectedPrivateChat.id,
        setMessages,
      );

      return () => {
        unsubscribeMessages();
      };
    }
  }, []);

  // Scroll to the bottom of the messages container when new messages are added
  const handleSendClick = async (e) => {
    e.preventDefault(); //prevent reloading page on click
    await sendPrivateChatMessage(selectedPrivateChat.id, user, input, setInput);
  };

  // Format the timestamp to display the time
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Toggle the user options modal
  /*
  const toggleOptions = (index) => {
    setShowOptions((prev) => ({ ...prev, [index]: !prev[index] })); //take the key 'index' that exists in prev, and flip its value (if its true, it'll now be false)
  };*/

  // Close the user options modal when clicking outside of it
  const handleClickOutside = (event) => {
    if (!event.target.closest(".options-menu")) {
      //.closest() checks if clicked elem or it's parent has class 'options-menu'
      setShowOptions({});
    }
  };

  // Handle the mention dropdown
  const handleSeeAccountInfo = async (uid) => {
    if (!uid) return;
    try {
      const userRef = doc(db, "publicUserInfo", uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setSelectedUser(userSnap.data());
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  // handle changing input content, tracks for @ to filter dropdown users
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    if (value.includes("@")) {
      const mentionPart = value.split("@").pop().toLowerCase();

      if (mentionPart) {
        //filter for only members that accepted invitation, then only return the members
        const filteredUsers = selectedPrivateChatMembers.filter((user) =>
          user.username.toLowerCase().includes(mentionPart.toLowerCase()),
        );
        setMentionDropdown(filteredUsers);
      } else {
        setMentionDropdown([]);
      }
    } else {
      setMentionDropdown([]);
    }
  };

  // Handle the mention dropdown
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  //settings page
  if (settingsOpen) {
    return (
      <PrivateSettings
        selectedPrivateChat={selectedPrivateChat}
        selectedPrivateChatMembers={selectedPrivateChatMembers}
        selectedPrivateChatPendingMembers={selectedPrivateChatPendingMembers}
        onClose={() => setSettingsOpen(false)}
        loadingChatSettings={loadingChatSettings}
      />
    );
  }

  // Handle the mention dropdown
  return (
    <>
      <div className="flex flex-col w-full h-full">
        <div className="px-4 py-2 rounded-lg shadow-lg z-50 text-center items-center ">
          <div className="mt-2 flex items-center mb-1 w-full">
            {/* Back button to go back to the private chat list */}
            <button
              onClick={() => {
                setSelectedPrivateChat(null);
                setSelectedPrivateChatMembers(null);
                setSelectedPrivateChatPendingMembers(null);
                setSelectedTab("privateTab");
              }}
            >
              ←
            </button>
            <h2 className="w-[125px] md:w-[150px] lg:w-[200px] mx-auto font-semibold text-base md:text-lg whitespace-nowrap overflow-hidden text-ellipsis block">
              {selectedPrivateChat.name || "Private Chat Name Not Set"}
            </h2>
            <button className="text-sm" onClick={() => setSettingsOpen(true)}>
              settings
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-around">
            <p className="w-full md:w-3/4 whitespace-nowrap overflow-hidden text-ellipsis block text-sm">
              {selectedPrivateChat.videoTitle || "No video title available"}
            </p>
            {videoUrl !== selectedPrivateChat.url &&
              selectedPrivateChat.url !== null && (
                <button
                  onClick={() => updateVideoUrl(selectedPrivateChat.url)} //need to setVideoUrl too
                  className="mt-2 px-3 py-1 text-sm text-white bg-primary rounded hover:bg-accent"
                >
                  load video
                </button>
              )}
          </div>
        </div>

        <div className="flex-grow overflow-y-scroll">
          <Messages
            privacyLevel="private"
            chatId={selectedPrivateChat.id}
            messages={messages}
            formatTimestamp={formatTimestamp}
            handleSeeAccountInfo={handleSeeAccountInfo}
            isPopupOpen={isPopupOpen}
            setIsPopupOpen={setIsPopupOpen}
          />
          <div ref={messagesEndRef} />
        </div>
        <ChatInputForm
          input={input}
          handleInputChange={handleInputChange}
          handleSendClick={handleSendClick}
          mentionDropdown={mentionDropdown}
        />
        <UserInfoModal
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />
      </div>
    </>
  );
};

export default PrivateChat;
